import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell, Camera, Calendar, Trophy, Crown, Heart, Sparkles, Check } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { photos_ready: Camera, event_tomorrow: Calendar, contest_won: Trophy, vip_approved: Crown, match_new: Heart, generic: Sparkles };
const COLOURS = { photos_ready: "text-magenta", event_tomorrow: "text-skyx", contest_won: "text-lime", vip_approved: "text-orangex", match_new: "text-pink", generic: "text-white" };

export default function FanNotificationBell() {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const load = () => api.get("/notifications?limit=20").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 45000);
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onDown); };
  }, []);

  const openNotif = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setData((d) => ({ ...d, unread: Math.max(0, d.unread - 1), notifications: d.notifications.map((x) => x.id === n.id ? { ...x, read: true } : x) }));
    }
    if (n.broadcast_id) {
      api.post("/notifications/push-track", { broadcast_id: n.broadcast_id, kind: "tap" }).catch(() => {});
    }
    if (n.link) nav(n.link);
  };
  const readAll = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    setData((d) => ({ ...d, unread: 0, notifications: d.notifications.map((x) => ({ ...x, read: true })) }));
  };

  // Compute viewport-safe fixed position for the dropdown panel.
  const panelStyle = (() => {
    if (!open || !btnRef.current) return { display: "none" };
    const rect = btnRef.current.getBoundingClientRect();
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    const panelWidth = Math.min(360, vw - 16); // 8px gutter each side
    // Anchor right edge of panel to right edge of button, clamp within viewport.
    let right = Math.max(8, vw - rect.right);
    // If the panel would push off the left edge, nudge right.
    if (rect.right - panelWidth < 8) right = Math.max(8, vw - panelWidth - 8);
    return {
      position: "fixed",
      top: rect.bottom + 8,
      right,
      width: panelWidth,
      zIndex: 70,
    };
  })();

  return (
    <div className="relative" data-testid="fan-notif-bell">
      <button ref={btnRef} onClick={() => setOpen((v) => !v)} data-testid="fan-notif-btn" title="Notifications"
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 border border-white/15 hover:border-lime/40 transition-colors">
        <Bell className="w-4 h-4 text-white" />
        {data.unread > 0 && (
          <span data-testid="fan-notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-magenta text-white text-[10px] font-bold flex items-center justify-center border-2 border-bg">{data.unread > 99 ? "99+" : data.unread}</span>
        )}
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <div ref={panelRef} style={panelStyle} className="glass rounded-2xl overflow-hidden shadow-2xl" data-testid="fan-notif-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="font-heading font-bold">Notifications</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{data.unread} unread</div>
            </div>
            {data.unread > 0 && (
              <button onClick={readAll} data-testid="fan-notif-read-all" className="text-[10px] font-mono uppercase tracking-widest text-lime hover:text-white"><Check className="w-3 h-3 inline" /> Mark all read</button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {data.notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
                <div className="text-sm font-heading font-bold">All quiet.</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">We'll ping you when photos land, an event is near, or you win something.</div>
              </div>
            ) : data.notifications.map((n) => {
              const Icon = ICONS[n.kind] || Sparkles;
              const color = COLOURS[n.kind] || "text-white";
              return (
                <button key={n.id} onClick={() => openNotif(n)} data-testid={`fan-notif-${n.id}`}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/5 border-l-2 ${n.read ? "border-transparent" : "border-magenta bg-white/5"}`}>
                  <Icon className={`w-4 h-4 mt-0.5 ${color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm leading-snug">{n.title}</div>
                    <div className="text-[11px] text-muted mt-0.5 line-clamp-2">{n.body}</div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-muted mt-1">{new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-magenta mt-2 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
